import type {
  AssetPath, IStorageProvider as EngineStorageProviderInterface
} from '@gnsx/genesys.js';

/**
 * Common interfaces for file storage operations
 * This interface abstracts the storage operations across different storage providers
 * like Firebase Storage and local file server.
 */

/**
 * Storage provider type enum
 */
export const StorageProviderType = {
  FIREBASE: 'firebase',
  S3: 's3',
  FILE_SERVER: 'file-server',
  PUBLIC_FOLDER: 'public-folder',
} as const;
export type StorageProviderType = (typeof StorageProviderType)[keyof typeof StorageProviderType];

/**
 * Live state provider type enum for game session managers
 * This is separate from storage providers to allow independent configuration
 */
export const LiveStateProviderType = {
  FIREBASE: 'firebase',
} as const;
export type LiveStateProviderType = (typeof LiveStateProviderType)[keyof typeof LiveStateProviderType];

export interface FileDownloadOptions {
  asBlob?: boolean; // If true, returns Blob, otherwise returns string/ArrayBuffer based on content
}

export interface FileExistsOptions {
  silent?: boolean;
}

/**
 * Interface for storage providers
 * This interface defines the common operations that should be implemented
 * by different storage providers (Firebase Storage, File Server)
 */
export interface IStorageProvider extends EngineStorageProviderInterface {
  /**
   * The type of storage provider
   */
  providerType: StorageProviderType;

  /**
   * Handle file system changes
   * @param callback Function to call when file system changes occur
   * @returns A function to unsubscribe from file system changes
   */
  onFileSystemChange(callback: (event: FileChangeEvent) => void): () => void;

  /**
   * Downloads a file from the specified path
   * @param path The path of the file to download
   * @param options Download options
   */
  downloadFile(
    path: AssetPath,
    options?: FileDownloadOptions
  ): Promise<Blob | string | ArrayBuffer>;

  /**
   * Checks if a file or directory exists.
   * @param path The path to check
   * @param options Existence check options
   */
  exists(path: AssetPath, options?: FileExistsOptions): Promise<boolean>;

  /**
   * Deletes a file or directory
   * @param path The path to delete
   * @param recursive Whether to recursively delete subdirectories (for directories)
   */
  deleteItem(path: AssetPath, recursive?: boolean): Promise<void>;

  /**
   * Creates a directory at the specified path
   * @param path The path where to create the directory
   */
  createDirectory(path: AssetPath): Promise<void>;

  /**
   * Checks if the storage provider requires a project context
   */
  requireProjectContext(): boolean;
}

export const FileChangeType = {
  FILE: 'file',
  FOLDER: 'folder'
} as const;
export type FileChangeType = (typeof FileChangeType)[keyof typeof FileChangeType];

export const FileChangeAction = {
  CREATED: 'created',
  MODIFIED: 'modified',
  DELETED: 'deleted'
} as const;
export type FileChangeAction = (typeof FileChangeAction)[keyof typeof FileChangeAction];

export interface FileChangeEvent {
  type: FileChangeType;
  path: string;
  action: FileChangeAction;
}
